import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  SearchMdIcon,
  Globe01Icon,
  MessageSmileCircleIcon,
  Settings01Icon,
  CheckIcon,
} from '@untitledui-icons/react/line'
import { cn } from '@/lib/utils'
import { Progress } from '@/components/ui/Progress'
import { Button } from '@/components/base/buttons'
import type { OnboardingStatus } from '@/hooks/api/useOnboarding'

interface ProcessingStepProps {
  status: OnboardingStatus | null
  isLoading: boolean
  error: string | null
  taskId: string | null
}

/**
 * Step 3: Processing/Review
 * Shows the progress of the onboarding task and completion state.
 */
export function ProcessingStep({ status, isLoading, error, taskId }: ProcessingStepProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const progress = status?.progress ?? 0
  const isCompleted = status?.status === 'completed'
  const isError = status?.status === 'error' || !!error

  // Processing steps with progress thresholds (translated)
  const processingSteps = useMemo(
    () => [
      { threshold: 20, labelKey: 'resources.onboarding.processing.finding_business', icon: SearchMdIcon },
      { threshold: 40, labelKey: 'resources.onboarding.processing.analyzing_website', icon: Globe01Icon },
      { threshold: 60, labelKey: 'resources.onboarding.processing.training_assistant', icon: MessageSmileCircleIcon },
      { threshold: 80, labelKey: 'resources.onboarding.processing.setting_up_account', icon: Settings01Icon },
      { threshold: 100, labelKey: 'resources.onboarding.processing.ready_to_serve', icon: CheckIcon },
    ],
    []
  )

  // Get current step based on progress
  const currentStep = useMemo(() => {
    const index = processingSteps.findIndex((step) => progress < step.threshold)
    return index === -1 ? processingSteps.length - 1 : index
  }, [progress, processingSteps])

  const CurrentIcon = processingSteps[currentStep]?.icon ?? SearchMdIcon
  const currentLabel = t(processingSteps[currentStep]?.labelKey ?? 'resources.onboarding.processing.finding_business')

  // Error state
  if (isError) {
    return (
      <div className="mx-auto max-w-lg text-center">
        <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-error-100 dark:bg-error-900/20">
          <span className="text-3xl">❌</span>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          {t('resources.onboarding.processing.error_title')}
        </h2>
        <p className="mt-2 text-base text-gray-600 dark:text-gray-400">
          {error || status?.message || t('resources.onboarding.processing.error_default')}
        </p>
        <Button
          color="primary"
          size="lg"
          className="mt-6"
          onPress={() => window.location.reload()}
        >
          {t('resources.onboarding.processing.try_again')}
        </Button>
      </div>
    )
  }

  // Completed state
  if (isCompleted && taskId) {
    return (
      <div className="mx-auto max-w-lg text-center">
        <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-success-500">
          <CheckIcon className="h-8 w-8 text-white" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          {t('resources.onboarding.processing.assistant_ready')}
        </h2>
        <p className="mt-2 text-base text-gray-600 dark:text-gray-400">
          {t('resources.onboarding.processing.assistant_ready_description')}
        </p>
        <Button
          color="primary"
          size="lg"
          className="mt-6"
          onPress={() => navigate(`/assistants/create/about?onboarding=${taskId}`)}
        >
          {t('resources.onboarding.processing.get_started')}
        </Button>
      </div>
    )
  }

  // Processing state
  return (
    <div className="mx-auto max-w-lg">
      <h2 className="mb-6 text-center text-xl font-semibold text-gray-900 dark:text-gray-100">
        {t('resources.onboarding.processing.setting_up')}
      </h2>

      {/* Progress card */}
      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-900">
        {/* Current step display */}
        <div className="flex items-center gap-4">
          <div
            className={cn(
              'flex h-14 w-14 items-center justify-center rounded-full',
              'bg-brand-500 text-white'
            )}
          >
            <CurrentIcon className="h-7 w-7" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-gray-900 dark:text-gray-100">
              {currentLabel}
            </p>
            <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
              {status?.message || t('resources.onboarding.processing.initializing')}
            </p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-6">
          <Progress value={progress} size="lg" variant="brand" />
          <p className="mt-2 text-right text-sm text-gray-500 dark:text-gray-400">
            {progress}%
          </p>
        </div>
      </div>

      {/* Step list */}
      <div className="mt-6 space-y-2">
        {processingSteps.map((step, index) => {
          const isStepCompleted = progress >= step.threshold
          const isStepActive = index === currentStep

          return (
            <div
              key={step.labelKey}
              className={cn(
                'flex items-center gap-3 rounded-lg px-4 py-2',
                isStepCompleted && 'text-gray-900 dark:text-gray-100',
                isStepActive && 'bg-brand-50 dark:bg-brand-900/20',
                !isStepCompleted && !isStepActive && 'text-gray-400 dark:text-gray-600'
              )}
            >
              <step.icon className={cn('h-5 w-5', isStepCompleted && 'text-brand-500')} />
              <span className={cn('text-sm', isStepActive && 'font-medium')}>
                {t(step.labelKey)}
              </span>
              {isStepCompleted && (
                <CheckIcon className="ml-auto h-4 w-4 text-success-500" />
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default ProcessingStep
